// The box of text under a map pin: an order's pin_labels, plus its route tags
// when the map is in order_tags mode.
//
// This is rendered through the Google marker's `label` slot, which becomes a
// real DOM element the `.planning-marker-pin-labels` rule in index.css styles.
// It deliberately is NOT drawn into the pin's icon SVG. A marker's icon is one
// DOM element and it intercepts pointer events across its whole rectangle, so a
// ~100px-wide box baked into the icon blankets any pin behind it and makes it
// unhoverable. MarkerOptions.shape does not help: per the Maps API reference it
// only restricts which events the marker itself registers, not whether its
// element occludes. A DOM element with `pointer-events: none` is the only thing
// that actually lets the pointer through.
//
// Sizing, wrapping and the two-line cap all live in that CSS rule
// (`width: max-content`, `max-width`, `-webkit-line-clamp: 2`), which is why
// there is no text measurement here — the browser does it exactly, for whatever
// font actually resolves.

// Tags and labels share the one label slot a marker has, so they render as a
// single run. Tags lead: they come from a managed vocabulary and drive routing
// decisions, where labels are free-text annotation.
export const pinLabelBoxText = (tags, pinLabels) =>
    [...(tags || []), ...(pinLabels || [])]
        .map((entry) => String(entry ?? '').trim())
        .filter(Boolean)
        .join(', ');

// Trims, drops blanks, and de-duplicates case-insensitively in first-seen
// order — the same fold the input applies within a single order, so two orders
// labelled "Fragile" and "fragile" produce one box entry rather than two
// identical-looking ones.
export const dedupePinLabels = (labels) => {
    const seen = new Set();

    return (labels || []).reduce((acc, raw) => {
        const label = String(raw ?? '').trim();
        const folded = label.toLowerCase();

        if (label && !seen.has(folded)) {
            seen.add(folded);
            acc.push(label);
        }

        return acc;
    }, []);
};

// One pin can stand for several orders — every order on a route stop, or
// several orders sharing an address. Its box shows the union of their labels.
export const collectPinLabels = (orders) => dedupePinLabels((orders || []).flatMap((order) => order?.pin_labels || []));

// Pins are keyed per order, so several can land on one coordinate; each would
// otherwise hang its own box and they would stack on the same pixels with only
// the topmost readable. Returns the union per coordinate, for the caller to
// render once on the first pin there.
//
// Tags are grouped alongside labels because they share the one box: collapsing
// only the labels would leave the other pins each drawing a tags-only box, and
// the stacking would be right back.
export const groupPinLabelsByPosition = (descriptors) => {
    const groups = (descriptors || []).reduce((acc, descriptor) => {
        const { lat, lng } = descriptor?.position || {};
        const coord = `${lat},${lng}`;
        const group = acc[coord] || { tags: [], pinLabels: [] };

        group.tags.push(...(descriptor?.tags || []));
        group.pinLabels.push(...(descriptor?.pinLabels || []));
        acc[coord] = group;

        return acc;
    }, {});

    return Object.fromEntries(
        Object.entries(groups).map(([coord, { tags, pinLabels }]) => [
            coord,
            { tags: dedupePinLabels(tags), pinLabels: dedupePinLabels(pinLabels) },
        ])
    );
};

export const positionKey = (position) => `${position?.lat},${position?.lng}`;
